import {Injectable} from '@angular/core';
import {FormlyFieldConfig} from '@ngx-formly/core';
import {environment} from '../../../environments/environment';
import {Helper} from '../../helpers/helper.service';

export type TranslationIdMap = { [field: string]: { [locale: string]: number } };

/** The flattened form model: one key per translatable field, each holding one key per locale */
export type TranslationModel = { [field: string]: { [locale: string]: string } };

export interface TranslationRow {
  id: number | null;
  field: string;
  locale: string;
  content: string;
}

@Injectable()
export class TranslationFormlyService {

  // The shape is data-driven - one key per translatable field, each holding one key per
  // locale - so it is declared as an index signature rather than left as a bare object,
  // which no caller could read a field off without a cast.
  static prepareTranslationFields(fields, translations, languages): TranslationModel {
    const s: TranslationModel = {};
    for (const field of fields) {
      s[field] = {};
      languages.forEach(lang => {
        const key = lang.locale;
        s[field][key] = '';
      });
    }
    translations.forEach((item) => {
      const key = item.locale;
      const field = item.field;
      if (Helper.isDefined(s[field])) {
        s[field][key] = item.content;
      }
    });

    return s;
  }

  /**
   * D14/D16/D17: the flattened `{field: {locale: content}}` shape above is
   * bound directly as the formly form model, so it can't also carry each
   * row's `id` without breaking those bindings. Build a parallel
   * `{field: {locale: id}}` lookup instead, captured once at hydration time
   * and threaded back into reorganizeTranslations() on save -- without it,
   * every saved translation looks new to the API's row-matching logic
   * (TranslationCollectionReindexer), which inserts a duplicate instead of
   * updating the existing row and 500s on the table's unique constraint.
   */
  static buildTranslationIdMap(translations): TranslationIdMap {
    const ids: TranslationIdMap = {};
    (translations || []).forEach((item) => {
      if (!Helper.isDefined(ids[item.field])) {
        ids[item.field] = {};
      }
      ids[item.field][item.locale] = item.id;
    });
    return ids;
  }

  static findTranslationKeys(obj) {
    return Object.keys(obj.translations);
  }

  static findTranslationEntries(obj) {
    const entries = {};
    this.findTranslationKeys(obj).forEach(k => {
      entries[k] = Object.entries(obj.translations[k]);
    });
    return entries;
  }

  static reorganizeTranslations(fields, baseTranslations, translationIds: TranslationIdMap = {}): TranslationRow[] {
    const translations: TranslationRow[] = [];
    for (const field of fields) {
      const item = baseTranslations[field];
      for (const i in item) {
        if (item.hasOwnProperty(i)) {
          const id = translationIds && translationIds[field] ? translationIds[field][i] : undefined;
          translations.push({
            id: Helper.isDefined(id) ? id : null, field, locale: i, content: item[i]
          });
        }
      }
    }
    return translations;
  }
}
